import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {useMultipleValues} from 'Quoting/components/hooks/useMultipleValues';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {Map as IMap, List} from 'immutable';
import {
  getAmountValuePath,
  getGPLUBenefitScheduleBlockPropertyPaths,
  getGPLUUnderWritingBlockPropertyPaths,
  getParentPath,
} from '../PropertyPaths';
import {GPLU_CHILD_TERM_LIFE_FIELD_ID, GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP, GPLUCoverageCode} from '../../../PlanTemplates/GWL/gpluConstants';
import {createCTLRCoverageRangeValues, createCTLRCoverageSingleValue} from '../../../PlanTemplates/GWL/Plan1';
import {VALUE_NOT_AVAILABLE} from 'Quoting/utils/utilities';
import {removeQuoteFormErrorEntries} from 'Quoting/ducks/createQuote';
import {useModifyMultipleValuesQuoteFormErrors} from '../../../PlanTemplates/GWL/GWLUtilities';

export const BenefitScheduleBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const {modifyMVQuoteFormErrors} = useModifyMultipleValuesQuoteFormErrors();

  const {selectedPlan, defaultPlanValues, selectedCoverageIndex} = useSelector(
    (state: ReducerState) => state.quoteForm
  );

  const propertyPaths = getGPLUBenefitScheduleBlockPropertyPaths(selectedPlan?.planIndex, selectedCoverageIndex);
  const uwPropertyPaths = getGPLUUnderWritingBlockPropertyPaths(selectedPlan?.planIndex, selectedCoverageIndex);

  const {specifiedAmounts, handleSpecifiedAmounts} = useMultipleValues('GPLUBenefitScheduleMultipleValues');

  const dispatch = useDispatch();

  const selectedPlanCd =
    getQuoteFieldValue<string>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}].planCd`) ??
    VALUE_NOT_AVAILABLE;
  const defaultValues = defaultPlanValues?.get(selectedPlanCd)?.get(GPLUCoverageCode.CHILD_TERM_LIFE);
  const benefitScheduleKey: any = getQuoteFieldValue(
    `${getParentPath(selectedPlan?.planIndex, selectedCoverageIndex)}.benefitSchedule._key`
  );
  const [rootId, parentId] = [benefitScheduleKey?.get('rootId'), benefitScheduleKey?.get('id')];

  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');

  const handleBenefitTypeChange = (newType: string) => {
    const giAmount = getQuoteFieldValue<number>(getAmountValuePath(uwPropertyPaths.guaranteedIssueAmount));
    if (newType === 'RangeValues') {
      updateQuoteFieldValues([
        {path: propertyPaths.benefitTypeCd, newValue: newType},
        {path: propertyPaths.multipleValues, newValue: List()},
        {path: propertyPaths.singleValue, newValue: undefined},
        {
          path: propertyPaths.rangeValues,
          newValue: IMap(createCTLRCoverageRangeValues(defaultValues, rootId, parentId, giAmount)),
        },
      ]);
    } else if (newType === 'MultipleValues') {
      updateQuoteFieldValues([
        {path: propertyPaths.benefitTypeCd, newValue: newType},
        {path: propertyPaths.rangeValues, newValue: undefined},
        {path: propertyPaths.singleValue, newValue: undefined},
      ]);
    } else if (newType === 'SingleValue') {
      updateQuoteFieldValues([
        {path: propertyPaths.benefitTypeCd, newValue: newType},
        {path: propertyPaths.rangeValues, newValue: undefined},
        {path: propertyPaths.multipleValues, newValue: List()},
        {
          path: propertyPaths.singleValue,
          newValue: IMap(createCTLRCoverageSingleValue(defaultValues, rootId, parentId, giAmount)),
        },
      ]);
    }
    dispatch(
      removeQuoteFormErrorEntries([
        `${GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitMaxAmount}__${selectedPlan?.planKey}`,
        `${GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitMinAmount}__${selectedPlan?.planKey}`,
        `${GPLU_CHILD_TERM_LIFE_FIELD_ID.incrementAmount}__${selectedPlan?.planKey}`,
        `${GPLU_CHILD_TERM_LIFE_FIELD_ID.flatBenefitAmount}__${selectedPlan?.planKey}`,
        `${GPLU_CHILD_TERM_LIFE_FIELD_ID.specifiedBenefitAmount}__${selectedPlan?.planKey}`,
      ])
    );
  };

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'row-5e8a5fed-7788-4188-b68e-c684462de63f'},
      items: [
        {
          key: 'd7525833-3637-439a-bfe9-1af2ee113b64',
          hidden: situsStateCd !== 'NE',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Percentage of Participant Amount"
              id={propertyPaths.employeeAmountPct}
              value={getQuoteFieldValue(propertyPaths.employeeAmountPct)}
              onChange={e => updateQuoteFieldValue(propertyPaths.employeeAmountPct, Number(e.target.value))}
              type="number"
              adornment="percentage"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: '68297af9-3f28-42e6-94f8-ca2884919889',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Benefit Type"
              id={propertyPaths.benefitTypeCd}
              value={getQuoteFieldValue(propertyPaths.benefitTypeCd)}
              formFieldId={GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitTypeCd}
              onChange={e => handleBenefitTypeChange(e.target.value as string)}
              coverageCode={GPLUCoverageCode.CHILD_TERM_LIFE}
              dropdownVariant="AttributeValue"
              searchKey="GPLUBenefitSchedule.benefitTypeCd"
              disabled={situsStateCd === 'PR'}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'd7525833-3637-439a-bfe9-1af2ee113b74',
          hidden: getQuoteFieldValue(propertyPaths.benefitTypeCd) !== 'RangeValues',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Minimum Benefit Amount"
              id={propertyPaths.benefitMinAmount}
              value={getQuoteFieldValue(getAmountValuePath(propertyPaths.benefitMinAmount))}
              onChange={e =>
                updateQuoteFieldValues([
                  {path: getAmountValuePath(propertyPaths.benefitMinAmount), newValue: e.target.value},
                ])
              }
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitMinAmount}
              disabled={situsStateCd === 'PR'}
              type="number"
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'b6165956-6d9f-4bb8-b26f-fc52ba418551',
          hidden: getQuoteFieldValue(propertyPaths.benefitTypeCd) !== 'RangeValues',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount"
              id={propertyPaths.benefitMaxAmount}
              value={getQuoteFieldValue(getAmountValuePath(propertyPaths.benefitMaxAmount))}
              onChange={e =>
                updateQuoteFieldValues([
                  {path: getAmountValuePath(propertyPaths.benefitMaxAmount), newValue: e.target.value},
                ])
              }
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_CHILD_TERM_LIFE_FIELD_ID.benefitMaxAmount}
              disabled={situsStateCd === 'PR'}
              type="number"
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'bac581be-7124-40f1-9802-32b054a51940',
          hidden: getQuoteFieldValue(propertyPaths.benefitTypeCd) !== 'RangeValues',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Increment"
              id={propertyPaths.incrementAmount}
              value={getQuoteFieldValue(getAmountValuePath(propertyPaths.incrementAmount))}
              onChange={e =>
                updateQuoteFieldValue(getAmountValuePath(propertyPaths.incrementAmount), Number(e.target.value))
              }
              formFieldId={GPLU_CHILD_TERM_LIFE_FIELD_ID.incrementAmount}
              type="number"
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: '74e9f486-94dc-43e1-aba0-262d15ad76c0',
          hidden: getQuoteFieldValue(propertyPaths.benefitTypeCd) !== 'MultipleValues',
          element: (
            <QuoteFormElement
              elementType="MultiSelect"
              label="Specified Benefit Amounts"
              id={propertyPaths.multipleValues}
              value={specifiedAmounts}
              onChange={val => {
                handleSpecifiedAmounts(val);
                const guaranteedIssueAmount = getQuoteFieldValue<number>(getAmountValuePath(uwPropertyPaths.guaranteedIssueAmount));
                modifyMVQuoteFormErrors(
                  val,
                  guaranteedIssueAmount,
                  `${GPLU_CHILD_TERM_LIFE_FIELD_ID.specifiedBenefitAmount}__${selectedPlan?.planKey}`,
                  GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP[GPLUCoverageCode.CHILD_TERM_LIFE]
                );
              }}
              dropdownVariant="AttributeValue"
              searchKey="GPLUBenefitScheduleMultipleValues.specifiedAmount"
              formFieldId={GPLU_CHILD_TERM_LIFE_FIELD_ID.specifiedBenefitAmount}
              disabled={situsStateCd === 'PR'}
              coverageCode={GPLUCoverageCode.CHILD_TERM_LIFE}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: '55d036d9-b6aa-4a1d-b612-ac8e0e6edbf3',
          hidden: getQuoteFieldValue(propertyPaths.benefitTypeCd) !== 'SingleValue',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Flat Benefit Amount"
              id={propertyPaths.flatBenefitAmount}
              value={getQuoteFieldValue(getAmountValuePath(propertyPaths.flatBenefitAmount))}
              onChange={e =>
                updateQuoteFieldValues([
                  {path: getAmountValuePath(propertyPaths.flatBenefitAmount), newValue: e.target.value},
                ])
              }
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_CHILD_TERM_LIFE_FIELD_ID.flatBenefitAmount}
              disabled={situsStateCd === 'PR'}
              type="number"
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Benefit Schedule" collapsible withSeparator expanded>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
